import {clamp, includes, isNil} from 'ramda';

import {
  getFinancingControlLimits,
  type FinancingControlDimensions,
  type FinancingControlSelection,
} from './getFinancingControlLimits';

/**
 * Moves a financing selection onto what its term offers, so a term or balloon change never
 * produces a coordinate the backend refuses. Every other field of the selection is kept.
 *
 * - A balloon not offered on the term, or not leaving a financed amount with the current down
 *   payment, is replaced by the lowest valid balloon of the term.
 * - A down payment below the lowest bracket of (term, balloon) snaps up to that bracket. A higher
 *   one is kept (it prices by the floor rule) unless it exceeds the range maximum, which only
 *   happens when the `downPayment + balloon < 100` cap applies.
 * - The term itself is never changed; with nothing offered the selection is returned unchanged.
 */
export function constrainFinancingSelection<Selection extends FinancingControlSelection>(
  dimensions: FinancingControlDimensions,
  selection: Selection
): Selection {
  const validBalloons = getFinancingControlLimits(dimensions, selection).balloonPaymentPercentages;
  const termBalloons = getFinancingControlLimits(dimensions, {
    ...selection,
    downPaymentPercentage: null,
  }).balloonPaymentPercentages;
  const balloonPaymentPercentage =
    isNil(selection.balloonPaymentPercentage) ||
    !includes(selection.balloonPaymentPercentage, validBalloons)
      ? (validBalloons.at(0) ?? termBalloons.at(0) ?? selection.balloonPaymentPercentage)
      : selection.balloonPaymentPercentage;

  const downPaymentLimits = getFinancingControlLimits(dimensions, {
    ...selection,
    balloonPaymentPercentage,
  }).downPayment;

  if (isNil(downPaymentLimits)) {
    return {...selection, balloonPaymentPercentage};
  }

  return {
    ...selection,
    downPaymentPercentage: isNil(selection.downPaymentPercentage)
      ? downPaymentLimits.min
      : clamp(downPaymentLimits.min, downPaymentLimits.max, selection.downPaymentPercentage),
    balloonPaymentPercentage,
  };
}
